import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowDownUp, Plus, ShieldCheck, LogOut, Send, RefreshCw,
  Check, Clock, X as XIcon, ExternalLink, ArrowRightLeft, ArrowUpFromLine, AlertTriangle, Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useUserAuth, userApi, formatApiErrorDetail } from "../lib/UserAuthContext";
import { LOGO_URL, SIGNUP_URL, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_TEL } from "../lib/site";

const API_PUBLIC = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, d = 2) => Number.isFinite(+n)
  ? (+n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d })
  : "—";

const statusBadge = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};
const statusIcon = { completed: Check, paid: Check, pending: Clock, cancelled: XIcon, rejected: XIcon };

export default function Dashboard() {
  const { user, logout, refresh } = useUserAuth();
  const [rates, setRates] = useState(null);
  const [direction, setDirection] = useState("CAD_TO_NGN");
  const [sendAmount, setSendAmount] = useState("");
  const [receiveAmount, setReceiveAmount] = useState("");
  const [edited, setEdited] = useState("send");
  const [txs, setTxs] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [kycBlockedOpen, setKycBlockedOpen] = useState(false);

  const isKycOk = user?.kyc_status === "verified";
  const kycNeeded = !isKycOk;

  const loadRates = async () => {
    const { data } = await axios.get(`${API_PUBLIC}/rates`);
    setRates(data);
    return data;
  };
  const loadAll = async () => {
    setLoading(true);
    try {
      const [_, txsResp, wsResp, cvResp] = await Promise.all([
        loadRates(),
        userApi.get("/transactions"),
        userApi.get("/withdrawals"),
        userApi.get("/conversions"),
      ]);
      setTxs(txsResp.data);
      setWithdrawals(wsResp.data);
      setConversions(cvResp.data);
      await refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  // ---- Bidirectional send calculator ----
  const rateForDir = useMemo(() => {
    if (!rates) return 0;
    return direction === "CAD_TO_NGN" ? rates.cad_to_ngn_rate : rates.ngn_to_cad_rate;
  }, [rates, direction]);
  const fromCcy = direction === "CAD_TO_NGN" ? "CAD" : "NGN";
  const toCcy = direction === "CAD_TO_NGN" ? "NGN" : "CAD";
  const sourceBalance = direction === "CAD_TO_NGN" ? (user?.wallet_cad || 0) : (user?.wallet_ngn || 0);

  useEffect(() => {
    if (!rateForDir) return;
    if (edited === "send") {
      const n = parseFloat(sendAmount);
      if (Number.isFinite(n) && n > 0) setReceiveAmount((n * rateForDir).toFixed(toCcy === "NGN" ? 2 : 4));
      else if (sendAmount === "") setReceiveAmount("");
    } else {
      const n = parseFloat(receiveAmount);
      if (Number.isFinite(n) && n > 0) setSendAmount((n / rateForDir).toFixed(fromCcy === "NGN" ? 2 : 4));
      else if (receiveAmount === "") setSendAmount("");
    }
    // eslint-disable-next-line
  }, [sendAmount, receiveAmount, edited, rateForDir, toCcy, fromCcy]);

  const swap = () => {
    setDirection((d) => (d === "CAD_TO_NGN" ? "NGN_TO_CAD" : "CAD_TO_NGN"));
    setSendAmount(""); setReceiveAmount(""); setEdited("send");
  };

  const guardKyc = () => {
    if (!isKycOk) { setKycBlockedOpen(true); return false; }
    return true;
  };

  const submitTx = async () => {
    if (!guardKyc()) return;
    const amt = parseFloat(sendAmount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter an amount to send."); return; }
    if (amt > sourceBalance + 1e-9) { toast.error(`Insufficient ${fromCcy}. Deposit to top up.`); return; }
    setBusy(true);
    try {
      await userApi.post("/transactions", { direction, from_amount: amt });
      toast.success("Transaction created — our team will process it.");
      setSendAmount(""); setReceiveAmount("");
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="CashPark" className="h-8 w-8" />
            <span className="font-display font-bold text-[#0B192C]">CashPark <span className="text-[#FF6B00]">Inc</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline" data-testid="user-name-display">
              Hi, <span className="font-semibold text-[#0B192C]">{user?.first_name}</span>
            </span>
            <Link to="/history" data-testid="dashboard-history-link">
              <Button variant="outline" className="rounded-full h-9 px-4 text-sm">
                <Receipt size={14} className="mr-1.5" /> History
              </Button>
            </Link>
            <Button variant="outline" onClick={async () => { await logout(); window.location.href = "/"; }} className="rounded-full h-9 px-4 text-sm" data-testid="dashboard-logout-button">
              <LogOut size={14} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl font-bold text-[#0B192C]">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your wallets and send money home.</p>

        {/* KYC banner */}
        {kycNeeded && (
          <div className="mt-6 rounded-3xl border border-amber-200 bg-amber-50 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="kyc-banner">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-11 h-11 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div>
                <div className="font-display font-bold text-[#0B192C]">Complete KYC to unlock transfers</div>
                <p className="text-sm text-amber-800 mt-0.5">Deposits, withdrawals, conversions and transfers are locked until your identity is verified. Verification takes about 3 minutes.</p>
              </div>
            </div>
            <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer" data-testid="kyc-banner-verify-button">
              <Button className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold h-11 px-6"><ShieldCheck size={16} className="mr-1.5" /> Verify KYC now</Button>
            </a>
          </div>
        )}

        {/* Wallet cards */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-[#0B192C] rounded-3xl p-6 text-white" data-testid="wallet-cad-card">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-300">CAD wallet</div>
              <span className="text-2xl">🇨🇦</span>
            </div>
            <div className="mt-4 font-display text-3xl font-bold" data-testid="wallet-cad-balance">${fmt(user?.wallet_cad || 0, 2)}</div>
            <div className="text-xs text-slate-400 mt-1">Canadian Dollars</div>
          </div>
          <div className="bg-gradient-to-br from-[#FF6B00] to-[#E65C00] rounded-3xl p-6 text-white" data-testid="wallet-ngn-card">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-white/80">NGN wallet</div>
              <span className="text-2xl">🇳🇬</span>
            </div>
            <div className="mt-4 font-display text-3xl font-bold" data-testid="wallet-ngn-balance">₦{fmt(user?.wallet_ngn || 0, 2)}</div>
            <div className="text-xs text-white/80 mt-1">Nigerian Naira</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-3xl p-6 grid grid-cols-2 gap-2" data-testid="wallet-actions-card">
            <ActionButton onClick={() => { if (guardKyc()) setDepositOpen(true); }} icon={Plus} label="Deposit" testid="deposit-button" />
            <ActionButton onClick={() => { if (guardKyc()) setWithdrawOpen(true); }} icon={ArrowUpFromLine} label="Withdraw" testid="withdraw-button" />
            <ActionButton onClick={() => { if (guardKyc()) setConvertOpen(true); }} icon={ArrowRightLeft} label="Convert" testid="convert-button" />
            {!isKycOk ? (
              <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer" data-testid="verify-kyc-link">
                <ActionButton icon={ShieldCheck} label="Verify KYC" as="span" />
              </a>
            ) : (
              <ActionButton onClick={loadAll} icon={RefreshCw} label="Refresh" testid="dashboard-refresh" spinning={loading} />
            )}
          </div>
        </div>

        {/* Send calculator */}
        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8" data-testid="tx-calculator">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[#FF6B00]">Send to recipient</div>
                <h2 className="font-display text-2xl font-bold text-[#0B192C] mt-1">Send {fromCcy} → {toCcy}</h2>
              </div>
              <button onClick={loadRates} className="p-2 rounded-full hover:bg-slate-100 text-slate-500" aria-label="Refresh rate" data-testid="tx-refresh-rate">
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              </button>
            </div>

            <div className="mt-5 space-y-3">
              <BiField
                label="You send" ccy={fromCcy}
                balance={<>Balance: <span className="font-semibold text-[#0B192C]" data-testid="tx-source-balance">{fmt(sourceBalance, 2)} {fromCcy}</span></>}
                value={sendAmount}
                onChange={(v) => { setEdited("send"); setSendAmount(v); }}
                testid="tx-send-input"
              />
              <div className="flex justify-center -my-1">
                <button type="button" onClick={swap} data-testid="tx-swap" className="rounded-full bg-[#0B192C] hover:bg-[#162846] text-white p-2 shadow-md" aria-label="Swap"><ArrowDownUp size={18} /></button>
              </div>
              <BiField
                label="Recipient gets" ccy={toCcy}
                value={receiveAmount}
                onChange={(v) => { setEdited("receive"); setReceiveAmount(v); }}
                testid="tx-receive-input"
              />
            </div>

            <div className="mt-5 text-xs text-slate-500 flex items-center justify-between">
              <span>Rate: <span className="font-display font-semibold text-[#0B192C]" data-testid="tx-rate">1 {fromCcy} = {fmt(rateForDir, toCcy === "NGN" ? 2 : 8)} {toCcy}</span></span>
              <span>Min: $3 CAD</span>
            </div>

            <Button onClick={submitTx} disabled={busy || kycNeeded} data-testid="tx-submit-button" className="mt-5 w-full h-12 rounded-full bg-[#FF6B00] hover:bg-[#E65C00] text-white text-base font-semibold shadow-brand disabled:opacity-60">
              {busy ? "Submitting…" : (<><Send size={16} className="mr-2" /> Send</>)}
            </Button>
            {kycNeeded && <p className="mt-2 text-[11px] text-amber-700 text-center">Verify KYC to enable sending.</p>}
          </div>

          {/* Activity list */}
          <ActivityList
            loading={loading}
            txs={txs}
            withdrawals={withdrawals}
            conversions={conversions}
          />
        </div>
      </main>

      {/* ================= DIALOGS ================= */}
      <DepositDialog open={depositOpen} onOpenChange={setDepositOpen} />
      <WithdrawDialog
        open={withdrawOpen} onOpenChange={setWithdrawOpen} user={user}
        onSuccess={loadAll}
      />
      <ConvertDialog
        open={convertOpen} onOpenChange={setConvertOpen} user={user}
        onSuccess={loadAll}
      />
      <KycBlockedDialog open={kycBlockedOpen} onOpenChange={setKycBlockedOpen} />
    </div>
  );
}

/* ===== Small building blocks ===== */

function ActionButton({ icon: Icon, label, onClick, testid, spinning, as }) {
  const cls = "w-full h-full rounded-2xl border border-slate-200 hover:border-[#FF6B00] hover:bg-orange-50/40 flex flex-col items-center justify-center gap-1 py-3 transition-colors";
  const inner = (
    <>
      <Icon size={18} className={`text-[#FF6B00] ${spinning ? "animate-spin" : ""}`} />
      <span className="text-xs font-semibold text-[#0B192C]">{label}</span>
    </>
  );
  if (as === "span") return <span className={cls}>{inner}</span>;
  return <button type="button" onClick={onClick} data-testid={testid} className={cls}>{inner}</button>;
}

function BiField({ label, ccy, balance, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
        {balance && <span className="text-slate-500">{balance}</span>}
      </div>
      <div className="mt-1.5 flex items-stretch rounded-2xl border border-slate-200 focus-within:border-[#FF6B00] focus-within:ring-2 focus-within:ring-[#FF6B00]/20 overflow-hidden">
        <Input type="number" inputMode="decimal" min="0" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0.00" data-testid={testid} className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-xl font-semibold h-14 rounded-none" />
        <div className="flex items-center px-4 bg-slate-50 border-l border-slate-200 font-semibold text-[#0B192C]">{ccy}</div>
      </div>
    </label>
  );
}

function ActivityList({ loading, txs, withdrawals, conversions }) {
  const items = [
    ...txs.map((t) => ({ kind: "Send", id: t.id, when: t.created_at, status: t.status, left: `${t.from_currency === "CAD" ? "$" : "₦"}${fmt(t.from_amount)}`, right: `${t.to_currency === "NGN" ? "₦" : "$"}${fmt(t.to_amount)}`, sub: `${t.from_currency} → ${t.to_currency}` })),
    ...withdrawals.map((w) => ({ kind: "Withdraw", id: w.id, when: w.created_at, status: w.status, left: `${w.currency === "CAD" ? "$" : "₦"}${fmt(w.amount)}`, right: w.method, sub: w.account_details })),
    ...conversions.map((c) => ({ kind: "Convert", id: c.id, when: c.created_at, status: "completed", left: `${c.from_currency === "CAD" ? "$" : "₦"}${fmt(c.from_amount)}`, right: `${c.to_currency === "NGN" ? "₦" : "$"}${fmt(c.to_amount)}`, sub: `${c.from_currency} → ${c.to_currency}` })),
  ].sort((a, b) => (a.when < b.when ? 1 : -1)).slice(0, 20);
  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8" data-testid="activity-list">
      <h2 className="font-display text-xl font-bold text-[#0B192C]">Recent activity</h2>
      {loading ? (
        <p className="mt-4 text-sm text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500" data-testid="activity-empty">No activity yet. Deposit, send, withdraw or convert to get started.</p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-100">
          {items.map((i) => {
            const Icon = statusIcon[i.status] || Clock;
            return (
              <li key={`${i.kind}-${i.id}`} className="py-3 flex items-center gap-3" data-testid={`activity-row-${i.id}`}>
                <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FF6B00] font-display font-bold text-xs flex items-center justify-center shrink-0">{i.kind[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-[#0B192C]">{i.kind}: {i.left}{i.right ? ` → ${i.right}` : ""}</div>
                  <div className="text-xs text-slate-500 truncate">{new Date(i.when).toLocaleString()} · {i.sub}</div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-semibold ${statusBadge[i.status] || statusBadge.pending}`}>
                  <Icon size={12} /> {i.status}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <Link to="/" className="mt-5 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#FF6B00]">
        View website <ExternalLink size={12} />
      </Link>
    </div>
  );
}

function DepositDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="deposit-dialog" className="sm:max-w-md">
        <DialogHeader><DialogTitle>Deposit funds</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm text-slate-600">
          <p>Contact our team to deposit CAD or NGN. Once we confirm receipt, your wallet is credited within minutes.</p>
          <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</div>
            <a href={`mailto:${CONTACT_EMAIL}?subject=Deposit%20request`} className="block mt-1 font-display font-semibold text-[#0B192C] hover:text-[#FF6B00] break-all" data-testid="deposit-email-link">{CONTACT_EMAIL}</a>
          </div>
          <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone / WhatsApp</div>
            <a href={`tel:${CONTACT_PHONE_TEL}`} className="block mt-1 font-display font-semibold text-[#0B192C] hover:text-[#FF6B00]">{CONTACT_PHONE}</a>
          </div>
          <a href="https://wa.link/pxadlb" target="_blank" rel="noopener noreferrer" data-testid="deposit-whatsapp-link">
            <Button className="w-full rounded-full bg-[#25D366] hover:bg-[#1FB855] h-11 font-semibold">Open WhatsApp</Button>
          </a>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WithdrawDialog({ open, onOpenChange, user, onSuccess }) {
  const [currency, setCurrency] = useState("CAD");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("interac");
  const [accountName, setAccountName] = useState("");
  const [accountDetails, setAccountDetails] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const bal = currency === "CAD" ? (user?.wallet_cad || 0) : (user?.wallet_ngn || 0);

  useEffect(() => {
    // Reset method to sensible default when currency changes
    setMethod(currency === "CAD" ? "interac" : "bank_transfer");
    setAccountDetails("");
  }, [currency]);

  useEffect(() => {
    if (open) {
      setAmount(""); setAccountName(""); setAccountDetails(""); setNotes("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a valid amount."); return; }
    if (amt > bal + 1e-9) { toast.error(`Insufficient ${currency} balance.`); return; }
    if (!accountName.trim() || !accountDetails.trim()) { toast.error("Enter account name and payout details."); return; }
    setBusy(true);
    try {
      await userApi.post("/withdrawals", {
        currency, amount: amt, method,
        account_name: accountName.trim(),
        account_details: accountDetails.trim(),
        notes: notes.trim() || null,
      });
      toast.success("Withdrawal request submitted. We'll pay it out shortly.");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="withdraw-dialog" className="sm:max-w-md">
        <DialogHeader><DialogTitle>Withdraw funds</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="withdraw-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="withdraw-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CAD">CAD 🇨🇦</SelectItem>
                  <SelectItem value="NGN">NGN 🇳🇬</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Balance: <span className="font-semibold text-[#0B192C]">{currency === "CAD" ? "$" : "₦"}{fmt(bal)}</span></p>
            </div>
            <div>
              <Label>Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="withdraw-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {currency === "CAD" ? (
                    <>
                      <SelectItem value="interac">Interac e-Transfer</SelectItem>
                      <SelectItem value="bank_transfer">Bank transfer (CAD)</SelectItem>
                    </>
                  ) : (
                    <SelectItem value="bank_transfer">Nigerian bank transfer</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Amount ({currency})</Label>
            <Input type="number" inputMode="decimal" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="withdraw-amount" className="mt-1.5 h-11 rounded-xl" />
          </div>
          <div>
            <Label>Account holder name</Label>
            <Input value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="Name on the account" data-testid="withdraw-account-name" className="mt-1.5 h-11 rounded-xl" />
          </div>
          <div>
            <Label>{method === "interac" ? "Interac email" : (currency === "NGN" ? "Bank name + account number" : "Institution + account number")}</Label>
            <Input value={accountDetails} onChange={(e) => setAccountDetails(e.target.value)} placeholder={method === "interac" ? "you@example.com" : (currency === "NGN" ? "e.g. GTBank 0123456789" : "e.g. TD 001 12345678")} data-testid="withdraw-account-details" className="mt-1.5 h-11 rounded-xl" />
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything we should know…" className="mt-1.5 rounded-xl" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="withdraw-submit" className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold">
              {busy ? "Submitting…" : "Submit withdrawal"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConvertDialog({ open, onOpenChange, user, onSuccess }) {
  const [fromCcy, setFromCcy] = useState("CAD");
  const [amount, setAmount] = useState("");
  const [midRate, setMidRate] = useState(null); // {cad_to_ngn, ngn_to_cad}
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        // Public /rates endpoint returns base_rate_ngn_per_cad (pre-margin mid-market)
        const { data } = await axios.get(`${API_PUBLIC}/rates`);
        const cadToNgn = data.base_rate_ngn_per_cad;
        setMidRate({ cad_to_ngn: cadToNgn, ngn_to_cad: 1.0 / cadToNgn });
      } catch {}
      finally { setLoading(false); }
    })();
    setAmount("");
  }, [open, fromCcy]);

  const toCcy = fromCcy === "CAD" ? "NGN" : "CAD";
  const rate = midRate ? (fromCcy === "CAD" ? midRate.cad_to_ngn : midRate.ngn_to_cad) : 0;
  const bal = fromCcy === "CAD" ? (user?.wallet_cad || 0) : (user?.wallet_ngn || 0);
  const preview = rate && amount ? (parseFloat(amount) * rate) : 0;

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a valid amount."); return; }
    if (amt > bal + 1e-9) { toast.error(`Insufficient ${fromCcy} balance.`); return; }
    setBusy(true);
    try {
      await userApi.post("/conversions", { from_currency: fromCcy, from_amount: amt });
      toast.success(`Converted ${amt} ${fromCcy} to ${toCcy}.`);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="convert-dialog" className="sm:max-w-md">
        <DialogHeader><DialogTitle>Convert between wallets</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="convert-form">
          <div>
            <Label>From wallet</Label>
            <Select value={fromCcy} onValueChange={setFromCcy}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="convert-from"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CAD">CAD 🇨🇦</SelectItem>
                <SelectItem value="NGN">NGN 🇳🇬</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">Balance: <span className="font-semibold text-[#0B192C]" data-testid="convert-balance">{fromCcy === "CAD" ? "$" : "₦"}{fmt(bal)}</span></p>
          </div>
          <div>
            <Label>Amount ({fromCcy})</Label>
            <Input type="number" inputMode="decimal" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="convert-amount" className="mt-1.5 h-11 rounded-xl" />
          </div>
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>You get in {toCcy}</span>
              <span>{loading ? "Loading rate…" : `1 ${fromCcy} = ${fmt(rate, toCcy === "NGN" ? 2 : 8)} ${toCcy}`}</span>
            </div>
            <div className="font-display font-bold text-2xl text-[#0B192C] mt-1" data-testid="convert-preview">
              {toCcy === "NGN" ? "₦" : "$"}{fmt(preview, toCcy === "NGN" ? 2 : 4)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">Live mid-market rate. No margin on wallet-to-wallet conversions.</div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={busy || loading} data-testid="convert-submit" className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold">
              {busy ? "Converting…" : "Convert now"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function KycBlockedDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="kyc-blocked-dialog" className="sm:max-w-md">
        <DialogHeader><DialogTitle>KYC verification required</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm text-slate-600">
          <p>For your protection and to comply with FINTRAC/CBN regulations, you must complete a quick identity verification before any deposit, withdrawal, conversion or transfer.</p>
          <p>It takes about 3 minutes — you'll need a government-issued ID and a selfie.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Later</Button>
          <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer" data-testid="kyc-blocked-verify-button">
            <Button className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold">
              <ShieldCheck size={16} className="mr-1.5" /> Verify KYC now
            </Button>
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
